<template>
  <v-card class="rounded-xl bg-surface border border-white-05">
    <v-card-title class="pa-6 border-b border-white-05 d-flex align-center">
      <span class="text-h5 font-weight-black">Edit Profile</span>
      <v-spacer></v-spacer>
      <v-btn icon="mdi-close" variant="text" @click="$emit('close')"></v-btn>
    </v-card-title>

    <v-card-text class="pa-6">
      <v-form @submit.prevent="handleSave">
        <!-- Banner Upload -->
        <div class="mb-6 position-relative">
          <div class="text-overline text-grey-darken-1 mb-2">Profile Banner</div>
          <v-hover v-slot="{ isHovering, props }">
            <div 
              v-bind="props"
              class="banner-preview rounded-lg border border-dashed border-white-10 d-flex align-center justify-center cursor-pointer position-relative overflow-hidden"
              :style="bannerPreviewStyle"
              @click="triggerBannerUpload"
            >
              <v-fade-transition>
                <div v-if="isHovering" class="upload-overlay d-flex flex-column align-center justify-center">
                  <v-icon icon="mdi-camera" color="white" size="large"></v-icon>
                  <span class="text-caption mt-1 font-weight-bold">Change Banner</span>
                </div>
              </v-fade-transition>
              <div v-if="!bannerPreview" class="d-flex flex-column align-center opacity-50">
                <v-icon icon="mdi-image-plus" size="large" class="mb-1"></v-icon>
                <span class="text-caption">Add Banner Image</span>
              </div>
            </div>
          </v-hover>
          <input type="file" ref="bannerInput" class="d-none" accept="image/*" @change="onBannerChange" />
        </div>

        <div class="d-flex align-end gap-6 mb-8 mt-n8 pl-4">
          <!-- Avatar Upload -->
          <div class="position-relative">
            <v-hover v-slot="{ isHovering, props }">
              <v-avatar 
                v-bind="props"
                size="80" 
                class="avatar-preview border-4 border-surface cursor-pointer" 
                @click="triggerAvatarUpload"
              >
                <v-img v-if="avatarPreview" :src="avatarPreview"></v-img>
                <v-icon v-else icon="mdi-account" size="48" color="grey"></v-icon>
                <v-fade-transition>
                  <div v-if="isHovering" class="upload-overlay d-flex align-center justify-center">
                    <v-icon icon="mdi-camera" color="white"></v-icon>
                  </div>
                </v-fade-transition>
              </v-avatar>
            </v-hover>
            <input type="file" ref="avatarInput" class="d-none" accept="image/*" @change="onAvatarChange" />
          </div>
          <div class="pb-1">
            <div class="text-overline text-grey-darken-1">Avatar</div>
            <span class="text-caption opacity-50">Click to change</span>
          </div>
        </div>

        <v-text-field
          v-model="formData.displayName"
          label="Display Name"
          variant="solo-filled"
          rounded="lg"
          class="mb-4"
          bg-color="rgba(255,255,255,0.05)"
          placeholder="What should we call you?"
          counter="64"
        ></v-text-field>

        <v-textarea
          v-model="formData.description"
          label="Bio / Description"
          variant="solo-filled"
          rounded="lg"
          auto-grow
          rows="3"
          bg-color="rgba(255,255,255,0.05)"
          placeholder="Tell the community about yourself..."
          counter="256"
        ></v-textarea>
      </v-form>
    </v-card-text>

    <v-card-actions class="pa-6 border-t border-white-05">
      <v-spacer></v-spacer>
      <v-btn variant="text" class="text-none font-weight-bold" @click="$emit('close')">Cancel</v-btn>
      <v-btn 
        color="primary" 
        rounded="pill" 
        class="text-none font-weight-black px-8" 
        :loading="saving"
        @click="handleSave"
      >
        Save Profile
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue';
import { userProfile, updateProfile, uploadBlob } from '@/services/bluesky';

const emit = defineEmits(['close', 'saved']);

const saving = ref(false);
const formData = reactive({
  displayName: '',
  description: '',
});

const avatarInput = ref<HTMLInputElement | null>(null);
const bannerInput = ref<HTMLInputElement | null>(null);

const avatarFile = ref<File | null>(null);
const bannerFile = ref<File | null>(null);

const avatarPreview = ref('');
const bannerPreview = ref('');

const bannerPreviewStyle = computed(() => {
  if (bannerPreview.value) {
    return {
      backgroundImage: `url(${bannerPreview.value})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '120px'
    };
  }
  return { minHeight: '120px', background: 'rgba(255,255,255,0.03)' };
});

onMounted(() => {
  if (userProfile.value) {
    formData.displayName = userProfile.value.displayName || '';
    formData.description = userProfile.value.description || '';
    avatarPreview.value = userProfile.value.avatar || '';
    bannerPreview.value = userProfile.value.banner || '';
  }
});

const triggerAvatarUpload = () => avatarInput.value?.click();
const triggerBannerUpload = () => bannerInput.value?.click();

const onAvatarChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) {
    avatarFile.value = file;
    avatarPreview.value = URL.createObjectURL(file);
  }
};

const onBannerChange = (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (file) {
    bannerFile.value = file;
    bannerPreview.value = URL.createObjectURL(file);
  }
};

const handleSave = async () => {
  saving.value = true;
  try {
    let avatarBlob = undefined;
    let bannerBlob = undefined;

    if (avatarFile.value) {
      avatarBlob = await uploadBlob(avatarFile.value, avatarFile.value.type);
    }
    if (bannerFile.value) {
      bannerBlob = await uploadBlob(bannerFile.value, bannerFile.value.type);
    }

    await updateProfile({
      displayName: formData.displayName,
      description: formData.description,
      avatar: avatarBlob,
      banner: bannerBlob
    });

    emit('saved');
    emit('close');
  } catch (error) {
    console.error('Failed to update profile:', error);
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.banner-preview {
  transition: all 0.3s ease;
}

.upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 2;
}

.avatar-preview {
  z-index: 3;
}

.border-white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.border-white-02 {
  border-color: rgba(255, 255, 255, 0.02) !important;
}

.gap-6 {
  gap: 24px;
}
</style>
